/** Purple chrome for context/system-managed agents. */
export const SYSTEM_AGENT_BORDER_COLOR = '#7c3aed';

/** Dark chrome for the tenant Master Agent orchestrator. */
export const MASTER_AGENT_BORDER_COLOR = '#18181b';

/** Sentinel sender label for workflow/system-authored sessions and messages. */
export const SYSTEM_SENDER_NAME = 'System';

/** Scoped context-rail agents — always treated as system agents for avatar chrome. */
export const SCOPED_CONTEXT_AGENT_NAMES = new Set([
  'Workflow Operator',
  'Workflows Agent',
  'Table Agent',
  'Docs Agent',
  'Board Agent',
  'Artifact Agent',
  'Team Agent',
  'Agents Agent',
  'Files Agent',
  'Custom Functions Agent',
]);

/**
 * Discriminated avatar tier. Exactly one applies to any agent identity, so every
 * downstream display decision (border color, sprite palette, headwear) derives
 * from a single lookup rather than independent, hand-synchronized booleans.
 */
export const AGENT_TIER = Object.freeze({
  MASTER: 'master',
  SYSTEM_FIXED: 'system-fixed',
  SYSTEM_SCOPED: 'system-scoped',
  CUSTOMER: 'customer',
});

/** Avatar border color per tier (`null` = no tier border). */
export const TIER_BORDER_COLOR = Object.freeze({
  [AGENT_TIER.MASTER]: MASTER_AGENT_BORDER_COLOR,
  [AGENT_TIER.SYSTEM_FIXED]: SYSTEM_AGENT_BORDER_COLOR,
  [AGENT_TIER.SYSTEM_SCOPED]: SYSTEM_AGENT_BORDER_COLOR,
  [AGENT_TIER.CUSTOMER]: null,
});

const TIER_FLAGS = Object.freeze({
  [AGENT_TIER.MASTER]: { masterAgent: true, systemAgent: false, fixedSystemSprite: true },
  [AGENT_TIER.SYSTEM_FIXED]: { masterAgent: false, systemAgent: true, fixedSystemSprite: true },
  [AGENT_TIER.SYSTEM_SCOPED]: { masterAgent: false, systemAgent: true, fixedSystemSprite: false },
  [AGENT_TIER.CUSTOMER]: { masterAgent: false, systemAgent: false, fixedSystemSprite: false },
});

export function isMasterAgent(agent) {
  return agent?.agentClass === 'MASTER';
}

export function isScopedContextAgent(agentName) {
  return Boolean(agentName && SCOPED_CONTEXT_AGENT_NAMES.has(agentName));
}

function findAgentRecord({ agent, agentId, agentName, agents = [] }) {
  return (
    agent ??
    (agentId ? agents.find((entry) => entry.agentId === agentId) : null) ??
    (agentName ? agents.find((entry) => entry.name === agentName) : null) ??
    null
  );
}

/**
 * Resolve the single avatar tier for an agent record, id, or name.
 * Master wins over scoped/system; scoped context-rail agents keep name-seeded
 * sprites (so they sort into {@link AGENT_TIER.SYSTEM_SCOPED}, not SYSTEM_FIXED).
 * @param {{ agent?: object, agentId?: string, agentName?: string, agents?: object[] }} [params]
 * @returns {string} one of {@link AGENT_TIER}
 */
export function resolveAgentTier({ agent, agentId, agentName, agents = [] } = {}) {
  const matchedAgent = findAgentRecord({ agent, agentId, agentName, agents });
  if (isMasterAgent(matchedAgent)) {
    return AGENT_TIER.MASTER;
  }
  const name = agentName ?? matchedAgent?.name;
  if (isScopedContextAgent(name)) {
    return AGENT_TIER.SYSTEM_SCOPED;
  }
  if (matchedAgent?.systemAgent === true) {
    return AGENT_TIER.SYSTEM_FIXED;
  }
  return AGENT_TIER.CUSTOMER;
}

/**
 * Whether an agent should use Master Agent avatar chrome (dark border / black helmet).
 * @param {{ agent?: object, agentName?: string, agents?: object[] }} [params]
 */
export function resolveMasterAgentForDisplay(params) {
  return resolveAgentTier(params) === AGENT_TIER.MASTER;
}

/**
 * Whether an agent should use context/system-agent avatar chrome (purple border / white hardhat).
 * Master Agent is excluded — use {@link resolveMasterAgentForDisplay} instead.
 * @param {{ agent?: object, agentName?: string, agents?: object[] }} [params]
 */
export function resolveSystemAgentForDisplay(params) {
  return TIER_FLAGS[resolveAgentTier(params)].systemAgent;
}

/**
 * Whether an agent should use a fixed product sprite palette (not name-seeded).
 * Master Agent uses the master palette; non-scoped system agents use the system palette.
 * Scoped context-rail agents keep unique name-seeded sprites.
 * @param {{ agent?: object, agentName?: string, agents?: object[] }} [params]
 */
export function resolveFixedSystemSpriteForDisplay(params) {
  return TIER_FLAGS[resolveAgentTier(params)].fixedSystemSprite;
}

/** @deprecated Prefer resolveSystemAgentForDisplay */
export function isSystemAgentForDisplay(agent) {
  return resolveSystemAgentForDisplay({ agent });
}

/** @deprecated Prefer resolveSystemAgentForDisplay */
export function isSystemAgentForDisplayByName(agentName, agents = []) {
  return resolveSystemAgentForDisplay({ agentName, agents });
}

/**
 * Resolve avatar display props for an agent record, id, or name.
 * @param {{ agent?: object, agentId?: string, agentName?: string, agents?: object[] }} [params]
 * @returns {{ displayName: string, avatarUrl: string|null, tier: string, systemAgent: boolean, masterAgent: boolean, fixedSystemSprite: boolean }}
 */
export function resolveAgentDisplayProfile({
  agent,
  agentId,
  agentName,
  agents = [],
} = {}) {
  const matchedAgent = findAgentRecord({ agent, agentId, agentName, agents });
  const displayName = matchedAgent?.name || agentName || 'Agent';
  const tier = resolveAgentTier({ agent: matchedAgent, agentName: displayName, agents });

  return {
    displayName,
    avatarUrl: matchedAgent?.avatarUrl || null,
    tier,
    ...TIER_FLAGS[tier],
  };
}
